import { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Button, EmptyState, layoutStore, SpinnerLoader, Typography } from 'shared/ui'
import { BreakpointsEnum } from 'shared/constants/breakpoints'
import { useEntitiesContext } from 'entities/EntitiesExport'
import {
  ProviderPageUsers,
  useSearchPageContext,
} from 'pages/conversations/pages/search/context/searchContext'
import { SearchPageStore } from 'pages/conversations/pages/search/store/searchPageStore'
import { RecentSearches } from 'pages/conversations/pages/search/ui/RecentSearches'
import { Results } from 'pages/conversations/pages/search/ui/Results'
import { SearchInput } from 'pages/conversations/pages/search/ui/SearchInput'
import { SearchPageStateEnum } from 'pages/conversations/pages/search/store/types'
import {
  BreakpointsSearchPageDefaultEnum,
  BreakpointsSearchPageTopResultEnum,
} from 'pages/conversations/pages/search/constants/breakpoints'
import { logV2SearchEvent } from 'pages/conversations/pages/search/utils/logSearchEvent'
import { useConversationsPageLayoutContext } from 'pages/conversations/context/conversationsPageLayoutContext'
import styles from './styles.module.scss'

const SearchPageContent = observer(() => {
  const entities = useEntitiesContext()
  const {
    searchInputStore,
    recentSearchesStore,
    resultsStore,
    state,
    load,
    dispose,
    setVisibleUI,
    onOpenCreateContactModal,
  } = useSearchPageContext()
  const { pageLayoutStore } = useConversationsPageLayoutContext()

  useEffect(() => {
    if (state === SearchPageStateEnum.results) {
      layoutStore.setBreakpoints(BreakpointsSearchPageTopResultEnum)
      pageLayoutStore.setCustomCollapseWidth(BreakpointsEnum.L)
    } else {
      layoutStore.setBreakpoints(BreakpointsSearchPageDefaultEnum)
      pageLayoutStore.setCustomCollapseWidth(null)
    }
  }, [state, layoutStore.isXlView])

  useEffect(() => {
    setVisibleUI(true)
    load()

    const layout = document.querySelector<HTMLDivElement>('.SM_layout')

    if (layout) {
      layout.scrollLeft = 0
    }

    return () => {
      setVisibleUI(false)
      dispose()
      layoutStore.setDefault()
      pageLayoutStore.setCustomCollapseWidth(null)
    }
  }, [])

  const renderContent = () => {
    if (state === SearchPageStateEnum.loading) {
      const hasSearchQuery = searchInputStore.search.length > 0
      return (
        <div className={styles.emptyState}>
          <div className={styles.content}>
            <div className={styles.loading}>
              <SpinnerLoader ariaLabel={'SearchPageContent'} />
            </div>
            {hasSearchQuery && (
              <Typography
                ariaLabel={'SearchingFor'}
                variant={'body-rg-medium'}
                color={'var(--content-primary-primary)'}
                marginBottom={4}
              >
                {`Searching for "${searchInputStore.search}"`}
              </Typography>
            )}
            {hasSearchQuery && (
              <Typography
                ariaLabel={'SearchingFor'}
                variant={'body-md-medium'}
                color={'var(--content-primary-tertiary)'}
              >
                This may take a few seconds
              </Typography>
            )}
          </div>
        </div>
      )
    }

    if (state === SearchPageStateEnum.recent_searches) {
      return <RecentSearches store={recentSearchesStore} />
    }

    if (state === SearchPageStateEnum.empty_state) {
      return (
        <div className={styles.emptyState}>
          <EmptyState text={'Start typing to search contacts and messages'} />
        </div>
      )
    }

    if (state === SearchPageStateEnum.create_contact) {
      return (
        <div className={styles.emptyState}>
          <EmptyState
            variant='default'
            text={'No results found'}
            actions={
              <>
                <Button
                  text={'Create contact'}
                  onClick={() => {
                    onOpenCreateContactModal()

                    logV2SearchEvent(
                      'Search Page Result Clicked',
                      {
                        event_id: 'search_page_result_clicked',
                        SearchResultClickedType: 'Create Contact',
                      },
                      entities
                    )
                  }}
                  size={'medium'}
                  typeBtn={'contained'}
                  contained={'secondary'}
                  icon={'add'}
                />
              </>
            }
          />
        </div>
      )
    }

    return <Results store={resultsStore} />
  }

  return (
    <div className={styles.wrap}>
      <SearchInput store={searchInputStore} />
      {renderContent()}
    </div>
  )
})

export const SearchPage = observer(() => {
  const entities = useEntitiesContext()
  const [searchPageStore] = useState(() => new SearchPageStore(entities))

  return (
    <ProviderPageUsers value={searchPageStore}>
      <SearchPageContent />
    </ProviderPageUsers>
  )
})
